import { Progress } from "antd";
import styled, { css } from "styled-components";

interface IConfigurableCardContainerProps {
  cardType: string;
  contextClassName: string;
  starOnDetailedCardFooter: boolean;
  starOnDetailedCardHeader: boolean;
  nextLessonInfo: string;
}

export const ConfigurableCardContainer = styled.div<IConfigurableCardContainerProps>`
  --card-animation-duration: 300ms;

  width: 100%;
  display: flex;
  cursor: pointer;
  user-select: none;
  position: relative;
  padding-top: 56.25%;

  .image-container-configurable-card {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .wrapper {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: ${(props) =>
      props.cardType.includes("complete") ? "flex-start" : "flex-end"};
    transition: all var(--card-animation-duration) ease-in-out;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      user-select: none;
      border-radius: 6px;
      -webkit-user-drag: none;
    }

    .title-container {
      left: 0;
      right: 0;
      position: absolute;
      padding: 0 20px 20px;

      ${({ contextClassName }) =>
        contextClassName
          ? css`
              width: calc(100% - 35px);
            `
          : css`
              width: 100%;
            `}
    }

    h3.title {
      margin: 0;
      z-index: 3;
      width: 100%;
      color: #fff;
      font-size: 19px;
      overflow: hidden;
      position: relative;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      transition: transform ease-in-out 0.2s;
    }

    .star-icon {
      z-index: 7;
      right: 15px;
      width: 25px;
      height: 25px;
      bottom: 15px;
      color: #ffa800;
      font-size: 25px;
      position: absolute;
      transition: transform ease-in-out 0.2s;
    }

    .overlay {
      z-index: 2;
      width: 100%;
      height: 100%;
      position: absolute;
      border-radius: 6px;
      background: linear-gradient(
        to top,
        rgba(15, 15, 15, 0.6) 30%,
        transparent 80%
      );
    }

    .detailed-card {
      z-index: 4;
      min-height: max-content;
      display: none;
      margin-top: -20px;
      width: 100%;

      position: absolute;
      border-radius: 6px;
      align-items: center;

      transition: all var(--card-animation-duration) ease-in-out;

      animation: expand var(--card-animation-duration) both;

      flex-direction: column;
      box-shadow: 15px 15px 30px rgba(0, 0, 0, 0.05);
      background: ${({ theme }) =>
        theme.mode === "dark"
          ? "rgba(15, 15, 15, 0.8)"
          : "rgba(255, 255, 255, 0.7)"};

      backdrop-filter: blur(30px);

      .img-aspect-ratio-container {
        width: 100%;
        display: flex;
        position: relative;
        padding-top: 56.25%;

        .img-position-container {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          justify-content: center;

          img {
            z-index: 6;
            width: 100%;
            height: 100%;
            border-radius: 6px 6px 0 0;
          }
        }
      }

      .detailed-card-body {
        width: 100%;
        height: 100%;
        padding: 30px 28px;
        position: relative;

        .title-container {
          padding: 0;
          position: relative;
        }

        h3.title {
          width: 100%;
          font-size: 20px;
          margin: 0 0 10px;
          color: ${({ theme }) => theme.colors.textColor} !important;
        }

        ${({ starOnDetailedCardHeader }) =>
          starOnDetailedCardHeader
            ? css`
                .title-container {
                  width: calc(100% - 35px);
                }

                .star-icon {
                  top: 30px;
                  right: 22px;
                  bottom: auto;
                }
              `
            : css`
                .title-container {
                  width: 100%;
                }

                .star-icon {
                  right: 22px;
                }
              `}

        p.description {
          font-size: 16px;
          font-weight: 300;
          margin: 0 0 15px;
          line-height: 22px;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          overflow: hidden;
          color: ${({ theme }) => theme.colors.textColor};
        }

        .detailed-card-footer {
          width: 100%;
          display: flex;
          align-items: center;

          .detailed-card-footer-content {
            display: flex;
            align-items: center;

            ${({ starOnDetailedCardFooter }) =>
              starOnDetailedCardFooter
                ? css`
                    width: calc(100% - 35px);
                  `
                : css`
                    width: 100%;
                  `}

            span {
              margin: 0;
              font-size: 16px;
            }

            span.info {
              color: ${({ theme }) => theme.colors.textColor};
              font-weight: 300;
            }

            span.starting-at {
              margin-right: 6px;
            }

            span.price {
              color: ${({ theme }) => theme.colors.textColor};
              font-weight: 700;
            }
          }
        }
      }

      .next-lesson-info {
        .info {
          display: flex;
          align-items: flex-start;
          color: ${({ theme }) => theme.colors.textColor};
          strong {
            margin-right: 4px;
          }
        }
      }
    }
  }

  &:hover {
    .wrapper {
      h3.title,
      .star-icon {
        transform: translateY(-8px);
      }

      .detailed-card {
        z-index: 7;
        display: flex;
        transition: all var(--card-animation-duration) ease-in-out;
      }

      ${({ cardType }) =>
        cardType.includes("complete") &&
        css`
          .static-flag {
            display: none;
            transition: all var(--card-animation-duration) ease-in-out;
          }
        `};
    }
  }

  @keyframes expand {
    0% {
      scale: 1;
    }

    100% {
      scale: 1.03;
    }
  }

  @keyframes expandOnMobile {
    0% {
      transform: translateY(2%);
    }

    100% {
      transform: translateY(0);
    }
  }

  @media screen and (max-width: 475px) {
    .wrapper {
      .detailed-card {
        width: 100%;
        animation: expandOnMobile var(--card-animation-duration);
      }
    }
  }
`;

export const ProgressBar = styled(Progress)`
  margin: 0;
  font-size: 5px;

  .ant-progress-outer {
    margin: 0;
    padding: 0;

    & > div {
      border-radius: 0;
      background-color: ${({ theme }) =>
        theme.mode === "dark" ? "#212121" : "#f5f5f5"};

      .ant-progress-success-bg,
      .ant-progress-bg {
        border-top-left-radius: 0px;
        border-bottom-left-radius: 0px;
      }
    }
  }
`;
